import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import "./css/LoginFormModal.css"; // Reuse existing modal styles
import UserAPI from "../services/UserAPI";
import { validatePassword, checkPasswordMatch } from "../utils/PasswordUtil";
import PasswordStrengthMeter from "../utils/PasswordStrengthMeter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../i18n/LanguageContext";

function ChangePasswordModal({ onClose }) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-theme") || "light",
  );

  // Detect theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const currentTheme =
        document.documentElement.getAttribute("data-theme") || "light";
      setTheme(currentTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Handle confirm password changes
  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    const matchResult = checkPasswordMatch(newPassword, value);
    setPasswordMismatch(matchResult.hasError);
    setPasswordsMatch(matchResult.isComplete);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Validate inputs
    if (!currentPassword.trim()) {
      setErrorMessage(t("changePassword.error.enterCurrent"));
      return;
    }

    if (!newPassword.trim()) {
      setErrorMessage(t("changePassword.error.enterNew"));
      return;
    }

    // Validate new password strength using NIST-compliant PasswordUtil
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setErrorMessage(passwordValidation.message);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage(t("changePassword.error.mismatch"));
      return;
    }

    // Check if new password is different from current
    if (currentPassword === newPassword) {
      setErrorMessage(t("changePassword.error.samePassword"));
      return;
    }

    try {
      setIsLoading(true);

      // Call API to change password
      await UserAPI.changePassword(currentPassword, newPassword);

      toast.success(t("changePassword.success"), { duration: 5000 });
      onClose();
    } catch (error) {
      console.error("Error changing password:", error);

      // Handle specific error messages from the API
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else if (error.response?.status === 401) {
        setErrorMessage(t("changePassword.error.wrongPassword"));
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(t("changePassword.error.failed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("login-modal")) {
      onClose();
    }
  };

  return (
    <div
      className="login-modal change-password-modal"
      onClick={handleOutsideClick}
    >
      <div className="login-modal-content" data-theme={theme}>
        <button
          className="close"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>{t("changePassword.title")}</h2>
        <form onSubmit={handleSubmit}>
          {" "}
          <div className="form-group">
            <label htmlFor="currentPassword">
              {t("changePassword.currentLabel")}
            </label>
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                id="currentPassword"
                name="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t("changePassword.currentPlaceholder")}
                disabled={isLoading}
                className={errorMessage ? "error" : ""}
                required
              />
              <span
                className="eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">{t("changePassword.newLabel")}</label>
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("changePassword.newPlaceholder")}
                disabled={isLoading || !currentPassword.trim()}
                className={errorMessage ? "error" : ""}
                minLength={12}
                maxLength={30}
                required
              />
              <span
                className="eye-icon"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  opacity: !currentPassword.trim() ? 0.5 : 1,
                  cursor: !currentPassword.trim() ? "not-allowed" : "pointer",
                }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </span>
            </div>
            <PasswordStrengthMeter password={newPassword} />
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">
              {t("changePassword.confirmLabel")}
            </label>
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  handleConfirmPasswordChange(value);
                }}
                placeholder={t("changePassword.confirmPlaceholder")}
                disabled={isLoading || !currentPassword.trim()}
                style={{
                  borderColor: passwordMismatch ? "lightcoral" : "#ddd",
                }}
                minLength={12}
                maxLength={30}
                required
              />
              <span
                className="eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </span>
            </div>
            {passwordMismatch && confirmPassword && (
              <small
                style={{
                  color: "lightcoral",
                  display: "block",
                  marginTop: "4px",
                }}
              >
                {t("changePassword.mismatch")}
              </small>
            )}
            {!passwordMismatch &&
              confirmPassword &&
              newPassword &&
              confirmPassword === newPassword && (
                <small
                  style={{
                    color: "#28a745",
                    display: "block",
                    marginTop: "4px",
                    fontWeight: "600",
                  }}
                >
                  {t("changePassword.match")}
                </small>
              )}
          </div>
          <div className="password-requirements">
            <p>
              <strong>{t("changePassword.requirements.title")}</strong>
            </p>
            <ul>
              <li>{t("changePassword.requirements.length")}</li>
              <li>{t("changePassword.requirements.passphrase")}</li>
              <li>{t("changePassword.requirements.avoid")}</li>
              <li>{t("changePassword.requirements.example")}</li>
            </ul>
            <p
              style={{
                fontSize: "0.85rem",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              {t("changePassword.requirements.tip")}
            </p>
          </div>{" "}
          {errorMessage && (
            <p className="error-message visible">{errorMessage}</p>
          )}
          <button
            type="submit"
            className="login-button"
            disabled={
              isLoading ||
              !currentPassword.trim() ||
              !newPassword.trim() ||
              !confirmPassword.trim() ||
              newPassword !== confirmPassword ||
              passwordMismatch ||
              !validatePassword(newPassword).isValid
            }
          >
            {isLoading
              ? t("changePassword.changing")
              : t("changePassword.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
